import { useQuery } from "@tanstack/react-query";
import { api, endpoints } from "../lib/api";
import Topbar from "../components/Topbar";
import { euro, timeOnly, shortDate, initials } from "../lib/format";
import { AppointmentStatusBadge } from "../components/StatusBadge";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  Area, AreaChart,
} from "recharts";
import { TrendingUp, ArrowUpRight, Users, Calendar, Crown } from "lucide-react";
import { Link } from "react-router-dom";

const fetchMetrics = () => api.get(endpoints.metrics).then((r) => r.data);

function KpiCard({ eyebrow, value, suffix, delta, icon: Icon, tone = "default", testId }) {
  return (
    <div data-testid={testId} className="laka-card p-6 flex flex-col justify-between h-full">
      <div className="flex items-start justify-between">
        <span className="laka-eyebrow">{eyebrow}</span>
        <span
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ backgroundColor: tone === "gold" ? "#D6B36A" : "#F5EFE7" }}
        >
          <Icon size={16} strokeWidth={1.25} color={tone === "gold" ? "#1A1A1A" : "#1A1A1A"} />
        </span>
      </div>
      <div className="mt-6">
        <div className="font-display text-5xl leading-none" style={{ color: "#1A1A1A" }}>
          {value}
          {suffix && <span className="text-xl align-top ml-1" style={{ color: "#D6B36A" }}>{suffix}</span>}
        </div>
        {delta && (
          <div className="mt-3 flex items-center gap-1 text-xs" style={{ color: "#2D6A4F" }}>
            <TrendingUp size={12} strokeWidth={1.5} />
            <span>{delta}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["metrics"], queryFn: fetchMetrics });

  if (isLoading) return <LoadingSkeleton />;
  if (!data) return null;

  return (
    <div>
      <Topbar
        title="Buenos días, Ana."
        subtitle="Resumen operativo de LAKA Club Beauty — actualización en tiempo real."
        showLogoMark
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="kpi-grid">
        <KpiCard
          eyebrow="Ingresos del mes"
          value={euro(data.monthly_revenue)}
          icon={TrendingUp}
          tone="gold"
          delta="+12,4% vs. mes anterior"
          testId="kpi-monthly-revenue"
        />
        <KpiCard
          eyebrow="Nuevas clientas"
          value={data.new_clients}
          icon={Users}
          delta={`${data.total_clients} total`}
          testId="kpi-new-clients"
        />
        <KpiCard
          eyebrow="Próximas reservas"
          value={data.upcoming_count}
          icon={Calendar}
          delta="Próximos 7 días"
          testId="kpi-upcoming-appointments"
        />
        <KpiCard
          eyebrow="Membresías activas"
          value={data.active_memberships}
          icon={Crown}
          delta="Silver · Gold · Black"
          testId="kpi-active-memberships"
        />
      </div>

      {/* Trend + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <div className="lg:col-span-2 laka-card p-8" data-testid="revenue-chart-card">
          <div className="flex items-end justify-between mb-6">
            <div>
              <span className="laka-eyebrow">Evolución</span>
              <h3 className="font-display text-2xl mt-1">Ingresos mensuales</h3>
            </div>
            <div className="text-right">
              <div className="text-xs" style={{ color: "var(--laka-text)" }}>Servicios</div>
              <div className="text-sm font-medium">{euro(data.revenue_by_kind.service)}</div>
              <div className="text-xs mt-2" style={{ color: "var(--laka-text)" }}>Membresías</div>
              <div className="text-sm font-medium">{euro(data.revenue_by_kind.membership)}</div>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="lakaGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D6B36A" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#D6B36A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E8DFD1" vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} width={60} />
                <Tooltip
                  cursor={{ stroke: "#D6B36A", strokeWidth: 1, strokeDasharray: "3 3" }}
                  contentStyle={{ borderRadius: 8, border: "1px solid #E8DFD1" }}
                  formatter={(v) => euro(v)}
                />
                <Area type="monotone" dataKey="revenue" stroke="#D6B36A" strokeWidth={2} fill="url(#lakaGold)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="laka-card p-8 flex flex-col" data-testid="alerts-card">
          <span className="laka-eyebrow">Alertas</span>
          <h3 className="font-display text-2xl mt-1 mb-6">Requieren atención</h3>

          <AlertRow
            title="Pagos pendientes"
            value={data.pending_payments_count}
            hint="Facturación por revisar"
          />
          <AlertRow
            title="Renovaciones próximas"
            value={Math.max(1, Math.floor(data.active_memberships / 5))}
            hint="En los próximos 15 días"
          />
          <AlertRow
            title="Clientas inactivas"
            value={data.inactive_clients_count}
            hint="Sin visita en 60+ días"
            highlight
          />

          <Link to="/marketing" className="mt-auto pt-6" data-testid="dashboard-view-marketing">
            <div className="flex items-center justify-between text-sm border-t pt-4 laka-hairline">
              <span>Ver campañas sugeridas</span>
              <ArrowUpRight size={16} strokeWidth={1.5} style={{ color: "#D6B36A" }} />
            </div>
          </Link>
        </div>
      </div>

      {/* Upcoming appointments + Top services */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <div className="lg:col-span-2 laka-card p-8" data-testid="upcoming-appointments-card">
          <div className="flex items-end justify-between mb-6">
            <div>
              <span className="laka-eyebrow">Agenda</span>
              <h3 className="font-display text-2xl mt-1">Próximas citas</h3>
            </div>
            <Link to="/agenda" className="text-xs tracking-wider uppercase hover:text-[#D6B36A] transition-colors" data-testid="dashboard-view-agenda">
              Ver agenda →
            </Link>
          </div>

          <div className="space-y-2">
            {data.upcoming_appointments.length === 0 && <EmptyState label="Sin citas próximas" />}
            {data.upcoming_appointments.map((a) => (
              <div key={a.id} className="flex items-center gap-4 py-3 border-b laka-hairline last:border-0">
                <div className="w-14 text-center">
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--laka-text)" }}>
                    {shortDate(a.starts_at)}
                  </div>
                  <div className="font-display text-xl leading-none mt-1">
                    {timeOnly(a.starts_at)}
                  </div>
                </div>
                <div className="w-px h-10 laka-hairline border-l" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium" style={{ backgroundColor: "#E8DFD1", color: "#1A1A1A" }}>
                      {initials(a.client_name)}
                    </span>
                    <span className="font-medium text-sm truncate">{a.client_name}</span>
                  </div>
                  <div className="text-xs mt-1 truncate" style={{ color: "var(--laka-text)" }}>
                    {a.service_name} · {a.professional} · {a.room}
                  </div>
                </div>
                <AppointmentStatusBadge status={a.status} />
              </div>
            ))}
          </div>
        </div>

        <div className="laka-card p-8" data-testid="top-services-card">
          <span className="laka-eyebrow">Ranking</span>
          <h3 className="font-display text-2xl mt-1 mb-6">Servicios más vendidos</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.top_services} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" tickLine={false} axisLine={false} width={140} style={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "#F5EFE7" }} contentStyle={{ borderRadius: 8, border: "1px solid #E8DFD1" }} />
                <Bar dataKey="count" fill="#D6B36A" radius={[0, 4, 4, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function AlertRow({ title, value, hint, highlight }) {
  return (
    <div className="flex items-center justify-between py-3 border-b laka-hairline last:border-0">
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs" style={{ color: "var(--laka-text)" }}>{hint}</div>
      </div>
      <div
        className={`font-display text-3xl leading-none ${highlight ? "" : ""}`}
        style={{ color: highlight ? "#E63946" : "#1A1A1A" }}
      >
        {value}
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="py-8 text-center text-sm" style={{ color: "var(--laka-text)" }}>
      {label}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div>
      <Topbar title="Cargando…" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[0,1,2,3].map(i => <div key={i} className="laka-card p-8 h-40 animate-pulse" />)}
      </div>
    </div>
  );
}
